import { useEffect, useState } from 'react'
import { listCostLayerSummary } from '../../api/inventory'
import { getLocations } from '../../api/master'
import { fmtMoney } from '../../utils/format'
import { BackLink, DocHeader } from '../../components/PageParts'
import Button from '../../components/Button'
import ErrorBanner from '../../components/ErrorBanner'

export default function CostLayerSummary() {
  const [rows, setRows] = useState([])
  const [sku, setSku] = useState('')
  const [locationId, setLocationId] = useState('')
  const [groupByLoc, setGroupByLoc] = useState(false)
  const [splitByCost, setSplitByCost] = useState(true)
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => { getLocations().then(setLocations).catch(()=>setLocations([])) }, [])

  const refresh = async () => {
    setLoading(true); setErr('')
    try {
      const params = { group: (splitByCost ? (groupByLoc ? 'product_location_cost' : 'product_cost') : (groupByLoc ? 'product_location' : 'product')) }
      if (sku.trim()) params.code = sku.trim()
      if (locationId) params.location_id = Number(locationId)
      const data = await listCostLayerSummary(params)
      setRows(Array.isArray(data) ? data : [])
    } catch (e) {
      setErr(e?.message || 'Failed to load summary')
      setRows([])
    }
    setLoading(false)
  }
  useEffect(() => { refresh() }, [])

  const exportCSV = () => {
    const costLabel = splitByCost ? 'Unit Cost' : 'Weighted Unit Cost'
    const header = groupByLoc
      ? ['Product ID','SKU','Name','Location','Qty Remaining', costLabel, 'Value']
      : ['Product ID','SKU','Name','Qty Remaining', costLabel, 'Value']
    const lines = rows.map(r => groupByLoc
      ? [r.product_id, r.sku, r.name, r.location_name || r.location_id || '', r.qty_remaining, r.unit_cost, r.value]
      : [r.product_id, r.sku, r.name, r.qty_remaining, r.unit_cost, r.value]
    )
    const csv = [header, ...lines].map(a => a.map(x => (x==null?'':String(x))).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'product_cost_layer_summary.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const totalQty = rows.reduce((s,r)=>s+Number(r.qty_remaining||0),0)
  const totalVal = rows.reduce((s,r)=>s+Number(r.value||0),0)

  return (
    <div className="ci-form-page">
      <DocHeader
        back={<BackLink to="/inventory/stock" label="Stock" />}
        title="Cost summary"
        meta="Stock value from the cost batches, by item and optionally by location."
        actions={<>
          <Button variant="ghost" size="sm" onClick={exportCSV}>Export CSV</Button>
          <Button variant="primary" size="sm" loading={loading} onClick={refresh}>Reload</Button>
        </>}
      />
      <div style={{ display:'grid', gridTemplateColumns:'auto 160px auto 200px auto auto 1fr', gap:8, alignItems:'center', marginTop:8 }}>
        <label>SKU/Barcode</label>
        <input value={sku} onChange={e=>setSku(e.target.value)} onKeyDown={e=>{ if (e.key==='Enter') refresh() }} placeholder="P001 or barcode" />
        <label>Location</label>
        <select value={locationId} onChange={e=>setLocationId(e.target.value)} disabled={!groupByLoc}>
          <option value="">All</option>
          {(locations||[]).map(l => (<option key={l.location_id} value={l.location_id}>{l.location_id}: {l.name}</option>))}
        </select>
        <label style={{ justifySelf:'end' }}>Group by Location</label>
        <input type="checkbox" checked={groupByLoc} onChange={e=>setGroupByLoc(e.target.checked)} />
        <label style={{ justifySelf:'end' }}>Split by Unit Cost</label>
        <input type="checkbox" checked={splitByCost} onChange={e=>setSplitByCost(e.target.checked)} />
        {/* NRV overlay removed: layers already include NRV cohorts */}
        <Button variant="primary" size="sm" onClick={refresh}>Apply</Button>
      </div>
      {err && <ErrorBanner message={err} />}
      <div className="scroll-x" style={{ marginTop: 10 }}>
        <table className="table ds-table-pro">
          <thead>
            <tr>
              <th>Product ID</th>
              <th>SKU</th>
              <th>Name</th>
              {groupByLoc && <th>Location</th>}
              <th style={{ textAlign:'right' }}>Qty Remaining</th>
              <th style={{ textAlign:'right' }}>{splitByCost ? 'Unit Cost' : 'Weighted Unit Cost'}</th>
              <th style={{ textAlign:'right' }}>Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i}>
                <td>{r.product_id}</td>
                <td>{r.sku}</td>
                <td>{r.name}</td>
                {groupByLoc && <td>{r.location_name || r.location_id || ''}</td>}
                <td style={{ textAlign:'right' }}>{Number(r.qty_remaining||0).toLocaleString()}</td>
                <td style={{ textAlign:'right' }}>{fmtMoney(r.unit_cost)}</td>
                <td style={{ textAlign:'right' }}>{fmtMoney(r.value)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={groupByLoc ? 4 : 3}></td>
              <td style={{ textAlign:'right', fontWeight:600 }}>{Number(totalQty).toLocaleString()}</td>
              <td></td>
              <td style={{ textAlign:'right', fontWeight:600 }}>{fmtMoney(totalVal)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  )
}
